import React, { useMemo } from 'react'
import cls from 'classnames'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { URL_CONST } from 'utils/urlConsts'

import { ADDONS } from 'containers/App/constants'
import { selectActualFeatureFlags } from 'containers/App/selectors'
import AccountSetup from 'containers/Navigation/AccountSetup'
import GroupSettingsMenu from 'containers/Navigation/GroupSettingsMenu'
import HelpMenu from 'containers/Navigation/HelpMenu'
import SystemNotifications from 'containers/Navigation/SystemNotifications'

import { MiniIcon } from 'components/lib/Badge'
import {
  Deepfake,
  Dmarc,
  Encryption,
  Home,
  Incident,
  Mailbox,
  RedTeam,
  SimulationTraining,
  SpearPhishingAgent,
  ThreatLog,
} from 'components/lib/DesignSystem/Icons'
import Tag, { TAGS_TYPES } from 'components/lib/DesignSystem/Tag'
import { NavigationStyles } from 'components/styles/layouts/navigation.styles'

import DropdownItem from './items/DropdownItem'
import IconItem from './items/IconItem'

const MOBILE_BREAKPOINT = 750

// Restore original helpers
const isSelected = (pathname, url) => {
  // Don't mark as selected if we're in settings
  if (pathname?.includes(URL_CONST.settings.self)) {
    return false
  }
  return Array.isArray(url) ? url.some((u) => pathname?.includes(u)) : pathname?.includes(url)
}

const getNavItemClass = (selected) => cls(selected ? 'selected typo-subtitle-2' : 'typo-body-1')

function NavigationLink({ id, pathname, children, isNewTab, disabled }) {
  if (disabled) {
    return (
      <span id={id} className="navigation-item-content disabled-nav-link">
        {children}
      </span>
    )
  }
  return (
    <Link
      id={id}
      to={pathname}
      className="navigation-item-content"
      target={isNewTab ? '_blank' : '_self'}
    >
      {children}
    </Link>
  )
}

export const RegularNav = ({
  partner,
  companyName,
  userEmail,
  pathname,
  incidentsCount,
  userDropdownItems,
  hasLimitedPocView,
  incidentsVisibility,
  investigationVisibility,
  awarenessVisibility,
  mailboxesVisibility,
  groupSettingsVisibility,
  accountSetupVisibility,
  dmarcVisibility,
  encryptionPLGVisibility,
  redTeamVisibility,
  permissions,
  licenses,
  companyId,
  shouldShowPLGDF,
  isAccessRestricted,
  spearPhishingAgentVisibility,
}) => {
  const redTeamSelected = isSelected(pathname, URL_CONST.redTeam.self)
  const homeSelected = isSelected(pathname, [URL_CONST.dashboard.home, URL_CONST.partners.self])
  const mailboxesSelected = isSelected(pathname, URL_CONST.mailboxes.self)
  const awarenessSelected =
    isSelected(pathname, URL_CONST.awarenessCenter.self) || isSelected(pathname, '/campaigns/')
  const rcSelected = isSelected(pathname, URL_CONST.incidents.self)
  const settingsSelected = isSelected(pathname, URL_CONST.settings.self)
  const groupSettingsSelected = isSelected(pathname, URL_CONST.groupSettings.self)
  const threatLogSelected = isSelected(pathname, URL_CONST.investigation.threatLog)
  const dmarcSelected = isSelected(pathname, URL_CONST.dmarc.self)
  const encryptionPLGSelected = isSelected(pathname, [
    URL_CONST.encryptionPLG.self,
    URL_CONST.emailEncryption.self,
  ])
  const deepfakeSelected = isSelected(pathname, URL_CONST.deepfake.self)
  const spearPhishingAgentSelected = isSelected(pathname, URL_CONST.psima.self)
  const isAdmin = permissions?.admin
  const isDmarcManagementEnabled = licenses?.add_ons?.includes(ADDONS.DMARC_MANAGEMENT)
  const isDmarcEnabled = isAdmin && isDmarcManagementEnabled
  const pdmarcURL = `/members-api/power-dmarc/magic-link-login-redirect/?companyFilter=${companyId}`
  const ff = useSelector(selectActualFeatureFlags)

  const systemNotificationTitle = partner
    ? `System Notifications (${companyName})`
    : 'System Notifications'

  const MemoSystemNotifications = useMemo(
    () => <SystemNotifications title={systemNotificationTitle} />,
    [systemNotificationTitle]
  )

  const MemoHelpMenu = useMemo(() => <HelpMenu />, [])

  const MemoAccountSetup = useMemo(() => <AccountSetup />, [])

  const MemoGroupSettingsMenu = useMemo(() => <GroupSettingsMenu />, [])

  return (
    <>
      <div className="navigation-group center">
        <div
          className={cls('navigation-item', 'gt-p', getNavItemClass(homeSelected))}
          id="home_button"
        >
          <NavigationLink
            pathname={partner ? URL_CONST.partners.self : URL_CONST.dashboard.home}
            disabled={isAccessRestricted}
          >
            <Home height="20px" width="20px" fill="white" />
            <span>Home</span>
          </NavigationLink>
        </div>

        {incidentsVisibility && (
          <div className={cls('navigation-item', getNavItemClass(rcSelected))}>
            <NavigationLink
              pathname={URL_CONST.incidents.unclassified}
              id="resolution_center_button"
              disabled={isAccessRestricted}
            >
              <Incident height="20px" width="20px" fill="white" imageName="incident" />
              <span title="Incidents">Incidents</span>
              {!!incidentsCount && !hasLimitedPocView && (
                <MiniIcon top="0" left="auto" right="0" id="resolution-center-badge">
                  {incidentsCount > 99 ? '99+' : incidentsCount}
                </MiniIcon>
              )}
            </NavigationLink>
          </div>
        )}

        {investigationVisibility && (
          <div className={cls('navigation-item', getNavItemClass(threatLogSelected), 'gt-t')}>
            <NavigationLink
              pathname={URL_CONST.investigation.threatLog}
              id="threat_log_button"
              disabled={isAccessRestricted}
            >
              <ThreatLog height="20px" width="20px" fill="white" imageName="threat-log" />
              <span>Investigation</span>
            </NavigationLink>
          </div>
        )}

        {(shouldShowPLGDF || ff.DEEPFAKE_SERVICE) && (
          <div className={cls('navigation-item', 'gt-p', getNavItemClass(deepfakeSelected))}>
            <NavigationLink
              pathname={URL_CONST.deepfake.self}
              id="deepfake_navigation_button"
              disabled={isAccessRestricted}
            >
              <Deepfake />
              <span title="Deepfake">Deepfake</span>
            </NavigationLink>
          </div>
        )}

        {redTeamVisibility && (
          <div className={cls('navigation-item', 'gt-p', getNavItemClass(redTeamSelected))}>
            <NavigationLink
              pathname={URL_CONST.redTeam.self}
              id="red_team_navigation_button"
              disabled={isAccessRestricted}
            >
              <RedTeam height="20px" width="20px" />
              <span title="Red Team">Red Team</span>
              <Tag type={TAGS_TYPES.NEW} style={{ marginLeft: '8px' }} />
            </NavigationLink>
          </div>
        )}

        {awarenessVisibility && (
          <div className={cls('navigation-item', 'gt-p', getNavItemClass(awarenessSelected))}>
            <NavigationLink
              pathname={URL_CONST.awarenessCenter.campaigns}
              id="awareness_center_button"
              disabled={isAccessRestricted}
            >
              <SimulationTraining
                height="20px"
                width="20px"
                fill="white"
                imageName="time_study_time_2"
              />
              <span className="nav-sat-label-full" title="Simulation & Training">
                Simulation & Training
              </span>
              <span className="nav-sat-label-short" title="Simulation & Training">
                S&T
              </span>
            </NavigationLink>
          </div>
        )}

        {mailboxesVisibility && (
          <div className={cls('navigation-item', 'gt-p', getNavItemClass(mailboxesSelected))}>
            <NavigationLink
              pathname={URL_CONST.mailboxes.management}
              id="mailbox_management_button"
            >
              <Mailbox height="20px" width="20px" fill="white" imageName="mailbox" />
              <span title="Mailboxes">Mailboxes</span>
            </NavigationLink>
          </div>
        )}

        {dmarcVisibility && (
          <div className={cls('navigation-item', 'gt-p', getNavItemClass(dmarcSelected))}>
            <NavigationLink
              id="dmarc_navigation_button"
              isNewTab={isDmarcEnabled}
              pathname={isDmarcEnabled ? pdmarcURL : URL_CONST.dmarc.self}
              disabled={isAccessRestricted}
            >
              <Dmarc height="20px" width="20px" fill="white" imageName="dmarc" />
              <span title="DMARC">DMARC</span>
            </NavigationLink>
          </div>
        )}
        {encryptionPLGVisibility && (
          <div className={cls('navigation-item', 'gt-p', getNavItemClass(encryptionPLGSelected))}>
            <NavigationLink
              id="encryption_plg_navigation_button"
              pathname={URL_CONST.encryptionPLG.self}
              disabled={isAccessRestricted}
            >
              <Encryption height="20px" width="20px" fill="white" imageName="encryption" />
              <span title="Encryption">Encryption</span>
              <Tag type={TAGS_TYPES.NEW} style={{ marginLeft: '8px' }} />
            </NavigationLink>
          </div>
        )}

        {spearPhishingAgentVisibility && (
          <div
            className={cls('navigation-item', 'gt-p', getNavItemClass(spearPhishingAgentSelected))}
          >
            <NavigationLink pathname={URL_CONST.psima.self} id="spear_phishing_agent">
              <SpearPhishingAgent
                height="20px"
                width="20px"
                fill="white"
                imageName="spear_phishing_agent"
              />
              <span className="nav-psima-label-full" title="Phishing Simulation Agent">
                Phishing Simulation Agent
              </span>
              <span className="nav-psima-label-short" title="Phishing Simulation Agent">
                PSA
              </span>
              <Tag type={TAGS_TYPES.NEW} style={{ marginLeft: '8px' }} />
            </NavigationLink>
          </div>
        )}
      </div>
      <div className="navigation-group right">
        <IconItem
          title={systemNotificationTitle}
          iconURL={`${STATIC_BASE_URL}/static/webapp/icons/alarm_bell.svg`}
          iconID="nav_system_notifications"
          PopupComponent={MemoSystemNotifications}
        />

        <IconItem
          title="Help"
          iconURL={`${STATIC_BASE_URL}/static/webapp/icons/question_circle.svg`}
          iconID="open_knowledge_base"
          PopupComponent={MemoHelpMenu}
        />

        {accountSetupVisibility && (
          <IconItem
            title="Account Setup"
            iconURL={`${STATIC_BASE_URL}/static/webapp/icons/task_list_settings_work_office_companies_18_1.svg`}
            iconID="accountSetup-icon"
            badgeHasDone
            PopupComponent={MemoAccountSetup}
          />
        )}

        {groupSettingsVisibility ? (
          <IconItem
            title="Company & Group Settings"
            iconURL={`${STATIC_BASE_URL}/static/webapp/icons/cog_interface_essential.svg`}
            iconID="settings_button"
            active={groupSettingsSelected || settingsSelected}
            PopupComponent={MemoGroupSettingsMenu}
          />
        ) : (
          <NavigationLink
            pathname={URL_CONST.settings.profile}
            id="settings_button"
            label="Settings"
          >
            <IconItem
              title="Settings"
              iconURL={`${STATIC_BASE_URL}/static/webapp/icons/cog_interface_essential.svg`}
              active={settingsSelected}
            />
          </NavigationLink>
        )}

        {partner && (
          <div className="navigation-item no-hover">
            <div className="navigation-item-content mw150 lt-tablet-bold" title={partner.name}>
              {partner.name}
            </div>
          </div>
        )}
        <DropdownItem id="username_dropdown" text={userEmail} items={userDropdownItems} />
      </div>
    </>
  )
}

function Navigation({
  history,
  partner,
  companyName,
  userEmail,
  location,
  incidentsCount,
  userDropdownItems,
  hasLimitedPocView,
  permissions,
  incidentsVisibility,
  investigationVisibility,
  awarenessVisibility,
  mailboxesVisibility,
  groupSettingsVisibility,
  accountSetupVisibility,
  dmarcVisibility,
  encryptionPLGVisibility,
  deepfakeVisibility,
  redTeamVisibility,
  licenses,
  companyId,
  shouldShowPLGDF,
  isAccessRestricted,
  spearPhishingAgentVisibility,
}) {
  const handleNavigationClick = (url, options) => {
    if (!options || !options.disableForMobile || window.innerWidth > MOBILE_BREAKPOINT) {
      history.push(url)
    }
  }

  return (
    <NavigationStyles>
      <div
        className={cls('navigation', {
          dn: location.pathname === URL_CONST.kpi.self,
        })}
      >
        <span
          onClick={() =>
            handleNavigationClick(partner ? URL_CONST.partners.self : URL_CONST.dashboard.home, {
              disableForMobile: true,
            })
          }
          id="logo_button"
          className="navigation-logo cp"
        />
        <span id="hamburger_menu_button" className="icon navigation-toggle">
          <img src={`${STATIC_BASE_URL}/static/webapp/images/hamburger-icon.svg`} />
        </span>
        <div id="main_navigation_toolbar" className="navigation-content">
          <RegularNav
            partner={partner}
            companyName={companyName}
            userEmail={userEmail}
            incidentsCount={incidentsCount}
            userDropdownItems={userDropdownItems}
            pathname={location.pathname}
            hasLimitedPocView={hasLimitedPocView}
            incidentsVisibility={incidentsVisibility}
            investigationVisibility={investigationVisibility}
            awarenessVisibility={awarenessVisibility}
            mailboxesVisibility={mailboxesVisibility}
            groupSettingsVisibility={groupSettingsVisibility}
            accountSetupVisibility={accountSetupVisibility}
            dmarcVisibility={dmarcVisibility}
            encryptionPLGVisibility={encryptionPLGVisibility}
            deepfakeVisibility={deepfakeVisibility}
            redTeamVisibility={redTeamVisibility}
            permissions={permissions}
            licenses={licenses}
            companyId={companyId}
            shouldShowPLGDF={shouldShowPLGDF}
            isAccessRestricted={isAccessRestricted}
            spearPhishingAgentVisibility={spearPhishingAgentVisibility}
          />
        </div>
      </div>
    </NavigationStyles>
  )
}

export default Navigation
